import { useMutation } from '@tanstack/react-query';
import { useState, type ChangeEvent, type FormEvent } from 'react';

import { useDocumentTitle } from '@/hooks/useDocumentTitle.hook';
import { accountDeletionApi } from '@/services/accountDeletion.service';
import type { DeleteAccountStep } from '@/types/accountDeletion.types';
import {
  deletionCodeSchema,
  requestDeletionSchema,
} from '@/validators/accountDeletion.validator';

interface UseDeleteAccountPageResult {
  step: DeleteAccountStep;
  email: string;
  code: string;
  emailError: string | null;
  codeError: string | null;
  submitError: string | null;
  isSubmitting: boolean;
  onChangeEmail: (event: ChangeEvent<HTMLInputElement>) => void;
  onChangeCode: (event: ChangeEvent<HTMLInputElement>) => void;
  onSubmitEmail: (event: FormEvent<HTMLFormElement>) => void;
  onSubmitCode: (event: FormEvent<HTMLFormElement>) => void;
  onBack: () => void;
}

const GENERIC_ERROR = 'Something went wrong. Please try again.';

/** The axios layer flattens failures to a plain Error carrying only `message`. */
const messageOf = (error: unknown): string =>
  error instanceof Error && error.message ? error.message : GENERIC_ERROR;

export const useDeleteAccountPage = (): UseDeleteAccountPageResult => {
  useDocumentTitle(
    'Delete your account | Shoutt',
    'Request deletion of your Shoutt account and associated data.',
  );

  const [step, setStep] = useState<DeleteAccountStep>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const requestMutation = useMutation({
    mutationFn: accountDeletionApi.request,
    onSuccess: () => {
      setSubmitError(null);
      setStep('code');
    },
    onError: (error: unknown) => setSubmitError(messageOf(error)),
  });

  const confirmMutation = useMutation({
    mutationFn: accountDeletionApi.confirm,
    onSuccess: () => {
      setSubmitError(null);
      setStep('done');
    },
    onError: (error: unknown) => setSubmitError(messageOf(error)),
  });

  const onChangeEmail = (event: ChangeEvent<HTMLInputElement>): void => {
    setEmail(event.target.value);
    setEmailError(null);
    setSubmitError(null);
  };

  const onChangeCode = (event: ChangeEvent<HTMLInputElement>): void => {
    // Digits only, capped at 4 — the field is numeric, so silently dropping the rest is
    // less annoying than a validation error the user has to read.
    setCode(event.target.value.replace(/\D/g, '').slice(0, 4));
    setCodeError(null);
    setSubmitError(null);
  };

  const onSubmitEmail = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const parsed = requestDeletionSchema.safeParse({ email });
    if (!parsed.success) {
      setEmailError(parsed.error.errors[0]?.message ?? GENERIC_ERROR);
      return;
    }
    requestMutation.mutate(parsed.data);
  };

  const onSubmitCode = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const parsed = deletionCodeSchema.safeParse(code);
    if (!parsed.success) {
      setCodeError(parsed.error.errors[0]?.message ?? GENERIC_ERROR);
      return;
    }
    confirmMutation.mutate({ email: email.trim().toLowerCase(), otp: parsed.data });
  };

  const onBack = (): void => {
    setStep('email');
    setCode('');
    setCodeError(null);
    setSubmitError(null);
  };

  return {
    step,
    email,
    code,
    emailError,
    codeError,
    submitError,
    isSubmitting: requestMutation.isPending || confirmMutation.isPending,
    onChangeEmail,
    onChangeCode,
    onSubmitEmail,
    onSubmitCode,
    onBack,
  };
};
